// WhatsApp API Service
// Connects directly to Node.js WhatsApp server

class WhatsAppAPIService {
    constructor() {
        // Use environment variable for API URL
        // Automatically switches between development (localhost:3001) and production (Render)
        const envURL = process.env.REACT_APP_WHATSAPP_API_URL;

        // Determine server base (web) URL and API baseURL
        // Prefer explicit env var; if envURL includes the API path, strip it for the web base
        const defaultServerBase = 'https://loan-server-pfyk.onrender.com';

        if (envURL) {
            if (envURL.includes('/api/whatsapp')) {
                this.serverBase = envURL.replace(/\/api\/whatsapp\/?$/i, '');
                this.baseURL = envURL;
            } else {
                this.serverBase = envURL.replace(/\/$/, '');
                this.baseURL = `${this.serverBase}/api/whatsapp`;
            }
        } else {
            this.serverBase = defaultServerBase;
            this.baseURL = `${this.serverBase}/api/whatsapp`;
        }

        this.isConnected = false;
        console.log('📡 WhatsApp API URL:', this.baseURL);
    }

    // Return the server base (web) URL, e.g. https://loan-server-pfyk.onrender.com
    getServerBase() {
        return this.serverBase;
    }

    // Check if WhatsApp API is connected
    async checkConnection() {
        try {
            console.log(`🔗 Connecting to Node.js WhatsApp server: ${this.baseURL}`);

            const response = await fetch(`${this.baseURL}/status`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('✅ Node.js WhatsApp server response:', data);

            this.isConnected = data.connected;
            return data;

        } catch (error) {
            console.error('❌ Failed to connect to Node.js WhatsApp server:', error.message);
            this.isConnected = false;
            return {
                connected: false,
                error: `Failed to connect to Node.js WhatsApp server: ${error.message}`,
                serverUrl: this.baseURL
            };
        }
    }

    // Get QR code for WhatsApp Web authentication
    async getQRCode() {
        try {
            console.log(`📱 Getting QR code from Node.js server: ${this.baseURL}`);

            const response = await fetch(`${this.baseURL}/qr-code`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('📱 QR Code response:', data);

            return data;
        } catch (error) {
            console.error('❌ Failed to get QR code:', error);
            return { success: false, error: error.message };
        }
    }

    // Logout from WhatsApp
    async logout() {
        try {
            console.log('🚪 Logging out from WhatsApp...');

            const response = await fetch(`${this.baseURL}/logout`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('🚪 Logout response:', data);

            return data;
        } catch (error) {
            console.error('❌ Failed to logout:', error);
            return { success: false, error: error.message };
        }
    }

    // Send message to single contact
    async sendMessage(phoneNumber, message) {
        try {
            console.log(`📤 Sending message to ${phoneNumber} via Node.js server`);

            const response = await fetch(`${this.baseURL}/send-message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phone: phoneNumber,
                    message: message
                })
            });

            const result = await response.json();
            console.log('📤 Send message result:', result);

            return result;
        } catch (error) {
            console.error('❌ Failed to send WhatsApp message:', error);
            return { success: false, error: error.message };
        }
    }

    // Send message to multiple contacts
    async sendBulkMessages(contacts, message) {
        try {
            console.log(`📤 Sending bulk messages to ${contacts.length} contacts via Node.js server`);

            const response = await fetch(`${this.baseURL}/send-bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    contacts: contacts, // Array of {phone, name}
                    message: message
                })
            });

            const result = await response.json();
            console.log('📤 Bulk send result:', result);

            return result;
        } catch (error) {
            console.error('❌ Failed to send bulk WhatsApp messages:', error);
            return { success: false, error: error.message };
        }
    }

    // Format phone number for WhatsApp (remove special characters, add country code)
    formatPhoneNumber(phone, countryCode = '91') {
        // Remove all non-numeric characters
        const cleaned = phone.replace(/\D/g, '');

        // Add country code if not present
        if (!cleaned.startsWith(countryCode)) {
            return `${countryCode}${cleaned}`;
        }

        return cleaned;
    }

    // Format message for WhatsApp (handle emojis and formatting)
    formatMessage(message) {
        return message
            .replace(/\*([^*]+)\*/g, '*$1*') // Bold text
            .replace(/_([^_]+)_/g, '_$1_') // Italic text
            .replace(/~([^~]+)~/g, '~$1~'); // Strikethrough text
    }
}

const whatsappAPIService = new WhatsAppAPIService();
export default whatsappAPIService;